import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import {
  Banknote, AlertTriangle, Info, Loader2, CheckCircle2,
  ArrowRight, FileText, ShieldAlert, Sparkles,
} from 'lucide-react';

const money = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(n) || 0);

const HARDSHIP_LABELS = {
  medical_expenses: 'Unreimbursed medical expenses',
  primary_residence_purchase: 'Primary residence purchase (not mortgage)',
  post_secondary_education: 'Post-secondary education costs (next 12 mo.)',
  eviction_or_foreclosure: 'Prevention of eviction or foreclosure',
  funeral_expenses: 'Funeral or burial expenses',
  casualty_loss: 'Casualty loss repairs (primary residence)',
  disaster_recovery: 'Federally-declared disaster recovery',
};

/**
 * In-Service Withdrawal Panel — Prompt 5A.
 *
 * Supports the two UniFed Core in-service withdrawal types:
 *  • Age-Based (≥59½) — 5 U.S.C. § 8433(h); 5 C.F.R. Part 1650 Subpart C
 *  • Hardship          — 26 U.S.C. § 401(k)(2)(B)(i)(IV); 5 C.F.R. § 1650.32
 *
 * Shows 20% mandatory federal withholding (26 U.S.C. § 3405(c)) and the
 * 10% additional early-withdrawal tax (26 U.S.C. § 72(t)) when under 59½.
 */
export const InServiceWithdrawalPanel = () => {
  const [elig, setElig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banks, setBanks] = useState([]);
  const [recent, setRecent] = useState([]);

  // Form state
  const [wType, setWType] = useState('age_based');
  const [amount, setAmount] = useState('');
  const [source, setSource] = useState('pro_rata');
  const [statePct, setStatePct] = useState('');
  const [hardshipReason, setHardshipReason] = useState('');
  const [bankId, setBankId] = useState('');
  const [ackWithhold, setAckWithhold] = useState(false);
  const [ackTax, setAckTax] = useState(false);

  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const [successId, setSuccessId] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [e, b, r] = await Promise.allSettled([
        api.get('/participant/in-service-withdrawal/eligibility'),
        api.get('/participant/bank-accounts'),
        api.get('/participant/in-service-withdrawal/requests'),
      ]);
      if (e.status === 'fulfilled') setElig(e.value.data);
      if (b.status === 'fulfilled') {
        const list = b.value.data?.accounts || b.value.data || [];
        const active = Array.isArray(list) ? list.filter(a => a.status === 'active') : [];
        setBanks(active);
        const primary = active.find(a => a.isPrimary) || active[0];
        if (primary) setBankId(primary.id);
      }
      if (r.status === 'fulfilled') setRecent(r.value.data?.requests || []);
    } finally { setLoading(false); }
  };

  // Default the type based on eligibility
  useEffect(() => {
    if (elig && !elig.ageBasedEligible) setWType('hardship');
  }, [elig]);

  const canSubmit = useMemo(() => {
    if (!preview) return false;
    if (!ackWithhold || !ackTax) return false;
    if (wType === 'hardship' && !hardshipReason) return false;
    if (!bankId) return false;
    return true;
  }, [preview, ackWithhold, ackTax, wType, hardshipReason, bankId]);

  const handlePreview = async () => {
    setPreviewErr(''); setPreview(null); setSuccessId('');
    const amt = parseFloat(amount);
    if (!amt || amt < 1000) { setPreviewErr('Minimum withdrawal is $1,000.'); return; }
    if (wType === 'age_based' && elig && !elig.ageBasedEligible) {
      setPreviewErr('You are not yet age 59½. Age-based withdrawals are not available.');
      return;
    }
    if (wType === 'hardship' && elig && amt > (elig.hardshipMaxAmount || 0)) {
      setPreviewErr(`Hardship withdrawals are limited to employee contributions (${money(elig.hardshipMaxAmount)}).`);
      return;
    }
    setPreviewing(true);
    try {
      const body = {
        withdrawalType: wType,
        grossAmount: amt,
        source,
        stateWithholdingPct: statePct === '' ? null : parseFloat(statePct),
        hardshipReason: wType === 'hardship' ? hardshipReason : null,
      };
      const res = await api.post('/participant/in-service-withdrawal/preview', body);
      setPreview(res.data);
    } catch (e) {
      setPreviewErr(e.response?.data?.detail || 'Unable to compute preview.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setSubmitErr('');
    try {
      const body = {
        withdrawalType: wType,
        grossAmount: parseFloat(amount),
        source,
        stateWithholdingPct: statePct === '' ? null : parseFloat(statePct),
        hardshipReason: wType === 'hardship' ? hardshipReason : null,
        bankAccountId: bankId,
        acknowledgeWithholding: ackWithhold,
        acknowledgeTaxation: ackTax,
      };
      const res = await api.post('/participant/in-service-withdrawal/request', body);
      setSuccessId(res.data?.requestId || '');
      // refresh recent list
      const r = await api.get('/participant/in-service-withdrawal/requests');
      setRecent(r.data?.requests || []);
      // reset form
      setAmount(''); setPreview(null);
      setAckWithhold(false); setAckTax(false);
    } catch (e) {
      setSubmitErr(e.response?.data?.detail || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20";

  if (loading) {
    return (
      <div className="panel p-6" data-testid="in-service-withdrawal-panel-loading">
        <div className="flex items-center gap-2 text-[#6B7280] text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading in-service withdrawal options…
        </div>
      </div>
    );
  }

  return (
    <div className="panel p-5" data-testid="in-service-withdrawal-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Banknote size={16} className="text-[#1B2A4A]" />
          <h3 className="text-sm font-semibold text-[#1B2A4A]">In-Service Withdrawal</h3>
        </div>
        <span className="text-[10px] text-[#9CA3AF] font-mono">5 U.S.C. § 8433(h) · 26 U.S.C. § 72(t)</span>
      </div>

      {/* Eligibility row */}
      {elig && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="isw-eligibility">
          <div className={`rounded border px-3 py-2 ${elig.ageBasedEligible ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">Age-Based (≥59½)</div>
            <div className={`text-sm font-medium mt-0.5 ${elig.ageBasedEligible ? 'text-emerald-700' : 'text-[#6B7280]'}`}>
              {elig.ageBasedEligible ? 'Eligible' : `Not yet — current age ${elig.age}`}
            </div>
          </div>
          <div className="rounded border bg-amber-50 border-amber-200 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">Hardship Cap</div>
            <div className="text-sm font-mono font-medium text-amber-800 mt-0.5">{money(elig.hardshipMaxAmount)}</div>
          </div>
          <div className="rounded border bg-[#F0F1F3] border-[#D1D5DB] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">Available Balance</div>
            <div className="text-sm font-mono font-medium text-[#1B2A4A] mt-0.5">{money(elig.totalBalance)}</div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Withdrawal Type</label>
          <div className="flex gap-2">
            <button
              data-testid="isw-type-age-based"
              onClick={() => setWType('age_based')}
              disabled={elig && !elig.ageBasedEligible}
              className={`flex-1 px-3 py-2 border rounded text-xs font-medium transition-colors ${
                wType === 'age_based'
                  ? 'bg-[#1B2A4A] text-white border-[#1B2A4A]'
                  : 'bg-white text-[#1B2A4A] border-[#D1D5DB] hover:bg-gray-50'
              } ${elig && !elig.ageBasedEligible ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Age-Based (59½)
            </button>
            <button
              data-testid="isw-type-hardship"
              onClick={() => setWType('hardship')}
              className={`flex-1 px-3 py-2 border rounded text-xs font-medium transition-colors ${
                wType === 'hardship'
                  ? 'bg-[#1B2A4A] text-white border-[#1B2A4A]'
                  : 'bg-white text-[#1B2A4A] border-[#D1D5DB] hover:bg-gray-50'
              }`}
            >
              Hardship
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Gross Amount (USD)</label>
          <input
            data-testid="isw-amount-input"
            type="number"
            min={1000}
            step={100}
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="10000"
            className={inputCls}
          />
          <p className="text-[10px] text-[#9CA3AF] mt-1 font-mono">Minimum $1,000</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Source</label>
          <select
            data-testid="isw-source-select"
            value={source}
            onChange={e => setSource(e.target.value)}
            className={inputCls}
          >
            <option value="pro_rata">Pro-Rata (Traditional + Roth) — 5 C.F.R. Part 1650</option>
            <option value="traditional">Traditional only</option>
            <option value="roth">Roth only</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#1B2A4A] mb-1">State Withholding % (optional)</label>
          <input
            data-testid="isw-state-withholding"
            type="number"
            min={0}
            max={20}
            step={0.5}
            value={statePct}
            onChange={e => setStatePct(e.target.value)}
            placeholder="0"
            className={inputCls}
          />
        </div>

        {wType === 'hardship' && (
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Hardship Reason (required)</label>
            <select
              data-testid="isw-hardship-reason"
              value={hardshipReason}
              onChange={e => setHardshipReason(e.target.value)}
              className={inputCls}
            >
              <option value="">— Select qualifying need —</option>
              {(elig?.hardshipReasons || Object.keys(HARDSHIP_LABELS)).map(r => (
                <option key={r} value={r}>{HARDSHIP_LABELS[r] || r}</option>
              ))}
            </select>
            <p className="text-[10px] text-amber-700 mt-1 flex items-start gap-1">
              <ShieldAlert size={11} className="mt-0.5 shrink-0" />
              Hardship withdrawals are limited to employee contributions and are NOT eligible for rollover. 5 C.F.R. § 1650.32.
            </p>
          </div>
        )}

        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Disbursement Bank Account</label>
          {banks.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              <AlertTriangle size={12} />
              You must verify at least one bank account before requesting a disbursement.
            </div>
          ) : (
            <select
              data-testid="isw-bank-select"
              value={bankId}
              onChange={e => setBankId(e.target.value)}
              className={inputCls}
            >
              {banks.map(b => (
                <option key={b.id} value={b.id}>
                  {b.bankName} — {b.maskedAccount} ({b.type}){b.isPrimary ? ' · Primary' : ''}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Preview Button */}
      <div className="mt-4 flex items-center gap-3">
        <button
          data-testid="isw-preview-btn"
          onClick={handlePreview}
          disabled={previewing || !amount}
          className="px-4 py-2 bg-[#1B2A4A] text-white text-sm rounded hover:bg-[#2D3F5F] disabled:opacity-50 flex items-center gap-2"
        >
          {previewing ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
          Calculate Withholding
        </button>
        {previewErr && (
          <span className="text-xs text-red-700 flex items-center gap-1" data-testid="isw-preview-error">
            <AlertTriangle size={12} /> {previewErr}
          </span>
        )}
      </div>

      {/* Preview Results */}
      {preview && (
        <div className="mt-5 border-t border-[#E5E7EB] pt-5" data-testid="isw-preview-results">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Cell label="Gross" value={money(preview.grossAmount)} />
            <Cell label="Federal 20%" value={money(preview.federalWithholding)} tone="red" />
            <Cell label={`State ${preview.stateWithholdingPct || 0}%`} value={money(preview.stateWithholding)} tone="red" />
            <Cell label="Net to You" value={money(preview.netAmount)} tone="emerald" bold />
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]">
            <div className="rounded border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2">
              <span className="text-[9px] uppercase tracking-wider font-semibold text-[#6B7280]">Traditional Portion</span>
              <div className="font-mono text-[#1B2A4A] mt-0.5">{money(preview.traditionalPortion)}</div>
            </div>
            <div className="rounded border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2">
              <span className="text-[9px] uppercase tracking-wider font-semibold text-[#6B7280]">Roth Portion (tax-free if qualified)</span>
              <div className="font-mono text-[#1B2A4A] mt-0.5">{money(preview.rothPortion)}</div>
            </div>
          </div>

          {/* Tax Impact Summary — plain-language estimate */}
          <TaxImpactSummary preview={preview} />

          {preview.earlyWithdrawalPenaltyApplies && (
            <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="isw-early-penalty">
              <AlertTriangle size={12} className="text-red-600 mt-0.5 shrink-0" />
              <div className="text-[11px] text-red-800 leading-relaxed">
                <strong>Under 59½ — 10% additional tax applies.</strong> Estimated{' '}
                <span className="font-mono font-semibold">{money(preview.earlyWithdrawalPenaltyEstimate)}</span> owed at tax filing. 26 U.S.C. § 72(t).
              </div>
            </div>
          )}

          {/* Acknowledgements */}
          <div className="mt-4 space-y-2">
            <label className="flex items-start gap-2 text-[11px] text-[#1B2A4A] leading-relaxed cursor-pointer">
              <input
                data-testid="isw-ack-withhold"
                type="checkbox"
                checked={ackWithhold}
                onChange={e => setAckWithhold(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                I acknowledge the 20% mandatory federal withholding on the Traditional portion per 26 U.S.C. § 3405(c)
                {preview.stateWithholdingPct > 0 && <> and the {preview.stateWithholdingPct}% elected state withholding</>}.
              </span>
            </label>
            <label className="flex items-start gap-2 text-[11px] text-[#1B2A4A] leading-relaxed cursor-pointer">
              <input
                data-testid="isw-ack-tax"
                type="checkbox"
                checked={ackTax}
                onChange={e => setAckTax(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                I understand that distributions are reported on Form 1099-R and may be subject to additional
                taxation at filing, including the 10% early withdrawal tax under 26 U.S.C. § 72(t) if I am under 59½.
                {wType === 'hardship' && <> Hardship withdrawals are NOT eligible for rollover.</>}
              </span>
            </label>
          </div>

          {/* Submit */}
          <div className="mt-4 flex items-center gap-3">
            <button
              data-testid="isw-submit-btn"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="px-5 py-2 bg-emerald-700 text-white text-sm rounded hover:bg-emerald-800 disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Submit Withdrawal Request
            </button>
            {submitErr && (
              <span className="text-xs text-red-700 flex items-center gap-1" data-testid="isw-submit-error">
                <AlertTriangle size={12} /> {submitErr}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Success */}
      {successId && (
        <div className="mt-4 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded px-3 py-2.5" data-testid="isw-success">
          <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-xs text-emerald-800 leading-relaxed">
            Request <span className="font-mono font-semibold">{successId}</span> submitted for processing.
            You will receive a confirmation when funds are disbursed. Estimated 7 business days.
          </div>
        </div>
      )}

      {/* Recent Requests */}
      {recent.length > 0 && (
        <div className="mt-5 border-t border-[#E5E7EB] pt-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={12} className="text-[#6B7280]" />
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">Recent Requests</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="isw-recent-table">
              <thead>
                <tr className="text-left text-[#6B7280] border-b border-[#E5E7EB]">
                  <th className="py-1.5 pr-3 font-medium">ID</th>
                  <th className="py-1.5 pr-3 font-medium">Type</th>
                  <th className="py-1.5 pr-3 font-medium">Gross</th>
                  <th className="py-1.5 pr-3 font-medium">Net</th>
                  <th className="py-1.5 pr-3 font-medium">Status</th>
                  <th className="py-1.5 pr-3 font-medium">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(r => (
                  <tr key={r.id} className="border-b border-[#F3F4F6] last:border-0">
                    <td className="py-1.5 pr-3 font-mono text-[#1B2A4A]">{r.id}</td>
                    <td className="py-1.5 pr-3">{r.withdrawalType?.replace('_', '-') || '-'}</td>
                    <td className="py-1.5 pr-3 font-mono">{money(r.grossAmount)}</td>
                    <td className="py-1.5 pr-3 font-mono">{money(r.netAmount)}</td>
                    <td className="py-1.5 pr-3">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-medium">
                        {r.status}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 text-[#6B7280] font-mono">
                      {r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Citations */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded px-3 py-2 flex items-start gap-2">
        <Info size={12} className="text-blue-600 mt-0.5 shrink-0" />
        <p className="text-[10px] text-blue-800 leading-relaxed">
          <strong>Citations:</strong> 5 U.S.C. § 8433(h) (age-based); 5 C.F.R. § 1650.32 &amp; 26 C.F.R. § 1.401(k)-1(d)(3) (hardship);
          26 U.S.C. § 3405(c) (20% mandatory federal withholding); 26 U.S.C. § 72(t) (10% early withdrawal tax);
          26 U.S.C. § 402A(d) (qualified Roth). Pro-rata source treatment: 5 C.F.R. Part 1650.
        </p>
      </div>
    </div>
  );
};

const Cell = ({ label, value, tone, bold }) => {
  const toneCls =
    tone === 'red' ? 'text-red-700' :
    tone === 'emerald' ? 'text-emerald-700' :
    'text-[#1B2A4A]';
  return (
    <div className="rounded border border-[#E5E7EB] bg-white px-3 py-2">
      <div className="text-[9px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
      <div className={`font-mono mt-0.5 ${bold ? 'text-lg font-semibold' : 'text-sm'} ${toneCls}`}>{value}</div>
    </div>
  );
};

/**
 * TaxImpactSummary — plain-language estimated 2026 tax bill impact card.
 *
 * Computes:
 *   • Estimated total federal tax on Traditional portion using 2026 single-filer
 *     marginal brackets applied as if this withdrawal were the participant's
 *     only additional income of the year (conservative stacking estimate).
 *   • Delta vs. 20% mandatory withholding → refund due / additional owed.
 *   • Plus the 10% § 72(t) early-withdrawal tax if applicable.
 *
 * Assumptions are shown transparently to the user so they can consult a tax
 * advisor for personalized guidance.
 */
const TaxImpactSummary = ({ preview }) => {
  if (!preview) return null;
  const trad = Number(preview.traditionalPortion) || 0;
  const fedWithheld = Number(preview.federalWithholding) || 0;
  const earlyPenalty = preview.earlyWithdrawalPenaltyApplies
    ? Number(preview.earlyWithdrawalPenaltyEstimate) || 0
    : 0;

  // 2026 federal single-filer marginal brackets (projected CPI-indexed)
  // Source: IRS Rev. Proc. 2025-32 (projected)
  const BRACKETS_2026 = [
    { upTo: 12_150, rate: 0.10 },
    { upTo: 49_350, rate: 0.12 },
    { upTo: 105_250, rate: 0.22 },
    { upTo: 201_000, rate: 0.24 },
    { upTo: 255_350, rate: 0.32 },
    { upTo: 638_000, rate: 0.35 },
    { upTo: Infinity, rate: 0.37 },
  ];

  // Conservative: treat Traditional withdrawal as top-of-income stack at 22%
  // unless amount is smaller than a low bracket. For amounts under $49k, use 12%.
  // This gives user a reasonable ballpark without requiring their full income.
  let estimatedTaxRate;
  if (trad <= 49_350) estimatedTaxRate = 0.12;
  else if (trad <= 105_250) estimatedTaxRate = 0.22;
  else estimatedTaxRate = 0.24;

  const estimatedFederalTax = trad * estimatedTaxRate;
  const deltaAtFiling = estimatedFederalTax + earlyPenalty - fedWithheld;
  const owesMore = deltaAtFiling > 0.01;
  const getsRefund = deltaAtFiling < -0.01;

  return (
    <div className="mt-4 bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded p-4" data-testid="isw-tax-impact-summary">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={13} className="text-indigo-600" />
        <span className="text-[11px] uppercase tracking-wider font-semibold text-indigo-700">
          Tax Impact Summary — 2026 Filing
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        <div className="bg-white border border-[#E5E7EB] rounded px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider font-semibold text-[#6B7280]">Est. Marginal Rate</div>
          <div className="font-mono text-sm text-[#1B2A4A] mt-0.5" data-testid="isw-tax-rate">{(estimatedTaxRate * 100).toFixed(0)}%</div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider font-semibold text-[#6B7280]">Est. Federal Tax Owed</div>
          <div className="font-mono text-sm text-[#1B2A4A] mt-0.5" data-testid="isw-tax-owed">
            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(estimatedFederalTax)}
          </div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider font-semibold text-[#6B7280]">Already Withheld (20%)</div>
          <div className="font-mono text-sm text-[#1B2A4A] mt-0.5">
            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(fedWithheld)}
          </div>
        </div>
      </div>

      {/* Plain-language summary */}
      <div className="bg-white border border-[#E5E7EB] rounded px-3 py-2.5" data-testid="isw-tax-plain-summary">
        <p className="text-[11px] text-[#1B2A4A] leading-relaxed">
          <strong>Bottom line:</strong>{' '}
          {owesMore && (
            <span>
              If you take this withdrawal now, you&apos;ll likely owe an additional{' '}
              <span className="font-mono font-semibold text-red-700">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(deltaAtFiling)}
              </span>{' '}
              at tax filing in April 2027 — the 20% mandatory withholding isn&apos;t enough to cover your estimated
              total federal tax {earlyPenalty > 0 ? 'plus the 10% early-withdrawal tax' : ''}.
            </span>
          )}
          {getsRefund && (
            <span>
              Based on this estimate, the 20% mandatory withholding <strong>exceeds</strong> your likely federal tax.
              You may see a refund of approximately{' '}
              <span className="font-mono font-semibold text-emerald-700">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(deltaAtFiling))}
              </span>{' '}
              when you file.
            </span>
          )}
          {!owesMore && !getsRefund && (
            <span>
              The 20% mandatory withholding is approximately equal to your estimated federal tax on this amount — so you
              shouldn&apos;t owe much more or get much refund at filing.
            </span>
          )}
        </p>
      </div>

      <p className="mt-2 text-[9px] text-[#6B7280] leading-relaxed">
        <strong>How we estimated this:</strong> We applied the projected 2026 IRS marginal bracket (
        <span className="font-mono">{(estimatedTaxRate * 100).toFixed(0)}%</span>) to your Traditional portion. Your actual
        tax depends on your total annual income, filing status, state, deductions, and credits. This is a planning
        estimate — not tax advice. Consult a qualified tax professional before submitting.
      </p>
    </div>
  );
};
